import React from "react";
import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser";
import {
  createAttribute,
  getallAttributes,
  deleteAttribute,
  getSingleAttribute,
  updateAttribute,
  activateAttribute,
} from "../../redux/features/product/attributeSlice";
import { useState, useMemo, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import ButtonLoading from "../../loader/ButtonLoader";
import { useTable, usePagination, useGlobalFilter, useFilters } from "react-table";
import { GlobalFilter } from "../globalFilter";
import { MdDeleteForever, MdEditSquare } from "react-icons/md";
import { VscLayersActive } from "react-icons/vsc";
import { AiOutlinePlus } from "react-icons/ai";
import { Modal } from "bootstrap";
import ReactToPrint from "react-to-print";
import { SelectColumnFilter, multiSelectFilter } from "./dashBoard/SelectColumnFilter1";
import { ColumnToggle } from "./ColumnToggle";



// Initial form state
const initialState = {
  name: "",
  type: "text",
  options: [],
};

export const Attribute = () => {
  useRedirectLogOutUser();

  const dispatch = useDispatch();
  const componentRef = useRef();
  const addModalRef = useRef(null);
  const editModalRef = useRef(null);
  const deleteModalRef = useRef(null);
  const activateModalRef = useRef(null);

  const [attributeForm, setAttributeForm] = useState(initialState);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [activateId, setActivateId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [printView, setPrintView] = useState(false);
  const [newOption, setNewOption] = useState("");

  const { name, type, options } = attributeForm;

  const { attributes, attribute } = useSelector((state) => state.attribute);
  const { userType } = useSelector((state) => state.auth);
  const isAdmin = userType === "admin";

  useEffect(() =>{
    dispatch(getallAttributes());
  }, [dispatch]);

  useEffect(() => {
    if (attribute && editId) {
      setAttributeForm({
        name: attribute.name || "",
        type: attribute.type || "text",
        options: attribute.options || [],
      });
    }
  }, [attribute, editId,]);

  const handleChange = (e) => {
    setAttributeForm({
      ...attributeForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddOption = () => {
    if (newOption.trim() === "") return;
    setAttributeForm({
      ...attributeForm,
      options: [...options, newOption.trim()],
    });
    setNewOption("");
  };

  const handleRemoveOption = (index) => {
    const newOptions = options.filter((_, idx) => idx !== index);
    setAttributeForm({
      ...attributeForm,
      options: newOptions,
    });
  };

  const handleAddAttribute = async (e) => {
    e.preventDefault();
    if (!name) return toast.error("Please fill in the name");

    setIsLoading(true);
    await dispatch(createAttribute(attributeForm));
    setIsLoading(false);
    setAttributeForm(initialState);

    const modal = Modal.getInstance(addModalRef.current);
    modal.hide();
  };

  const handleUpdateAttribute = async (e) => {
    e.preventDefault();
    if (!name) return toast.error("Please fill in the name");

    await dispatch(updateAttribute({ id: editId, data: attributeForm }));

    const modal = Modal.getInstance(editModalRef.current);
    modal.hide();
  };

  const openAddModal = () => {
    setAttributeForm(initialState);
    document.activeElement?.blur();
    new Modal(addModalRef.current).show();
  };

  const openEditModal = (id) => {
    setEditId(id);
    setAttributeForm(initialState);
    document.activeElement?.blur();
    new Modal(editModalRef.current).show();
    dispatch(getSingleAttribute(id));
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    document.activeElement?.blur();
    new Modal(deleteModalRef.current).show();
  };

  const handleDeleteAttribute = async () => {
    await dispatch(deleteAttribute(deleteId));
    const modal = Modal.getInstance(deleteModalRef.current);
    modal.hide();
  };

  const openActivateModal = (id) => {
    setActivateId(id);
    document.activeElement?.blur();
    new Modal(activateModalRef.current).show();
  };
  const handleActivateAttribute = async () => {
    await dispatch(activateAttribute(activateId));
    const modal = Modal.getInstance(activateModalRef.current);
    modal.hide();
  }

  const handleBeforeGetContent = async () => setPrintView(true);

  // Table setup
  const columns = useMemo(() => {
    const baseColumns = [
      { Header: "No", accessor: "no", Cell: ({ row }) => row.index + 1 },
      { Header: "Name", accessor: "name", Filter: SelectColumnFilter, filter: "multiSelectFilter" },
      {
  Header: "Options",
  accessor: "options",
  Cell: ({ value }) => (
    <div className="d-flex flex-wrap gap-1">
      {value?.map((opt, i) => (
        <span
          key={i}
          className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-2 py-1 fs-6">
          {opt}
        </span>
      ))}
    </div>
  ),
},
{
  Header: "Is Active",
  accessor: "isActive",
  Cell: ({ value }) => (
    <span className={`badge ${value ? 'bg-success' : 'bg-danger'} bg-opacity-10 text-${value ? 'success' : 'danger'} rounded-pill px-2 py-1 fs-6`}>
      {value ? 'Active' : 'Inactive'}
    </span>
  ),
  Filter: SelectColumnFilter,
  filter: "multiSelectFilter",
},
    ];

    if (!printView && isAdmin) {
      baseColumns.push({
        Header: "Action",
        Cell: ({ row }) => (
          <>
            <span
              className="text-warning"
              style={{ padding: "5px", cursor: "pointer" }}
              title="edit attribute"
              onClick={() => openEditModal(row.original._id)}
            >
              <MdEditSquare size={24} />
            </span>
            
            <span title="deactivate attribute">
              {row.original.isActive ? (
                <span
                  className="text-danger"
                  style={{ padding: "5px", cursor: "pointer" }}
                  onClick={() => openDeleteModal(row.original._id)}
                >
                  <MdDeleteForever size={24} />
                </span>
              ) : (
                <span
                  className="text-success"
                  style={{ padding: "5px", cursor: "pointer" }}
                  title="activate attribute"
                  onClick={() => openActivateModal(row.original._id)}
                >
                  <VscLayersActive size={24} />
                </span>
              )}
            </span>
          </>
        ),
      });
    }

    return baseColumns;
  }, [printView, isAdmin]);

  const data = useMemo(() => attributes, [attributes]);
  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);
  const tableInstance = useTable(
    { columns, data, filterTypes, defaultColumn: { Filter: () => null }, autoResetPage: false, autoResetFilters: false, autoResetGlobalFilter: false },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
    pageCount,
    setGlobalFilter,
    pageOptions,
    setPageSize,
    state,
    prepareRow,
    allColumns,
    getToggleHideAllColumnsProps
  } = tableInstance;

  const { pageIndex, globalFilter, pageSize } = state;

  return (
    <div>
      {/* Add Modal */}
      <div className="modal fade" ref={addModalRef} tabIndex="-1" data-bs-focus="false">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">
            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3 border-1">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-light"
                  style={{ width: 48, height: 48}}
                >
                  <AiOutlinePlus color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Add Attribute</h3>
                  <small className="text-muted fs-5">Create a new attribute.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <form className="border-0" onSubmit={handleAddAttribute}>
              <div className="modal-body border-0 py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  <label className="h4">Attribute Name</label>
                  <input
                    type="text"
                    className="form-control form-control-lg border-2 mb-2"
                    name="name"
                    value={name}
                    onChange={handleChange}
                    placeholder="Enter attribute name"
                  />
                  <label className="h4">Attribute Type</label>
                  <select
                    className="form-select form-select-lg mb-2 fs-2"
                    name="type"
                    value={type}
                    onChange={handleChange}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="select">Select</option>
                  </select>

                  
                    <div className="mt-2">
                      <label className="h6">Options</label>
                      <div className="d-flex gap-2 mb-2">
                        <input
                          type="text"
                          className="form-control"
                          value={newOption}
                          onChange={(e) => setNewOption(e.target.value)}
                          placeholder="Add option"
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAddOption}
                        >
                          Add
                        </button>
                      </div>
                      <div>
                        {options.map((opt, idx) => (
                          <span
                            key={idx}
                            className="badge bg-secondary me-2 mb-2 fs-5"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleRemoveOption(idx)}
                          >
                            {opt} &times;
                          </span>
                        ))}
                      </div>
                    </div>
                </div>
              </div>

              <div className="modal-footer border-0 pt-0 gap-2">
                <button
                  type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                {isLoading ? (
                  <ButtonLoading
                    className="btn btn-primary flex-fill rounded-3 fw-medium py-2"
                    disabled
                  >
                    Loading...
                  </ButtonLoading>
                ) : (
                  <button className="btn btn-primary flex-fill rounded-3 fw-medium py-2">
                    Add
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <div className="modal fade" ref={editModalRef} tabIndex="-1" data-bs-focus="false">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">
            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3 border-1">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}
                >
                  <MdEditSquare color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Edit Attribute</h3>
                  <small className="text-muted fs-5">Update attribute information.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <form className="border-0" onSubmit={handleUpdateAttribute}>
              <div className="modal-body border-0 py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  <label className="h4">Attribute Name</label>
                  <input
                    type="text"
                    className="form-control form-control-lg border-2 mb-2"
                    name="name"
                    value={name}
                    onChange={handleChange}
                    placeholder="Enter attribute name"
                  />
                  <label className="h4">Attribute Type</label>
                  <select
                    className="form-select form-select-lg mb-2 fs-2"
                    name="type"
                    value={type}
                    onChange={handleChange}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="select">Select</option>
                  </select>

                  
                    <div className="mt-2">
                      <label className="h4">Options</label>
                      <div className="d-flex gap-2 mb-2">
                        <input
                          type="text"
                          className="form-control form-control-lg"
                          value={newOption}
                          onChange={(e) => setNewOption(e.target.value)}
                          placeholder="Add option"
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAddOption}
                        >
                          Add
                        </button>
                      </div>
                      <div>
                        {options.map((opt, idx) => (
                          <span
                            key={idx}
                            className="badge bg-secondary me-2 mb-2 fs-5"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleRemoveOption(idx)}
                          >
                            {opt} &times;
                          </span>
                        ))}
                      </div>
                    </div>
                </div>
              </div>

              <div className="modal-footer border-0 pt-0 gap-2">
                <button
                  type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button className="btn btn-primary flex-fill rounded-3 fw-medium py-2">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" ref={deleteModalRef} tabIndex="-1" data-bs-focus="false">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">
            <div className="modal-header border-0 pb-0">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fde2e2" }}
                >
                  <MdDeleteForever color="red" size={40} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Delete Attribute</h3>
                  <small className="text-muted fs-5">
                    This action cannot be undone.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <p className="mb-0 h-3">
                  Are you sure you want to delete this attribute? All associated
                  data will be permanently removed from your workspace.
                </p>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0 gap-2">
              <button
                className="btn btn-primary border flex-fill rounded-3 fw-medium py-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>

              <button
                className="btn btn-danger flex-fill rounded-3 fw-medium py-2"
                onClick={handleDeleteAttribute}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

        {/* Activate Modal */}
        <div className="modal fade" ref={activateModalRef} tabIndex="-1" data-bs-focus="false">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">
            <div className="modal-header border-0 pb-0">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fde2e2" }}
                >
                  <VscLayersActive color="green" size={40} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Activate Attribute</h3>
                  <small className="text-muted fs-5">
                    Activating this attribute will make it available for use again.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <p className="mb-0 h-3">
                  Are you sure you want to activate this attribute? It will be made available for use again.
                </p>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0 gap-2">
              <button
                className="btn btn-primary border flex-fill rounded-3 fw-medium py-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>

              <button
                className="btn btn-success flex-fill rounded-3 fw-medium py-2"
                onClick={handleActivateAttribute}
              >
                Activate
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive mt-5">
        <div className="d-flex justify-content-between align-items-center m-4 px-2 py-2 rounded-4 border" style={{ backgroundColor: "#faf8f5" }}>
          <h4 className="mb-0 fs-2">Attributes</h4>
          <div className="d-flex gap-2">
            <ReactToPrint
              trigger={() => <button className="btn btn-primary rounded-3">Print Table</button>}
              content={() => componentRef.current}
              onBeforeGetContent={handleBeforeGetContent}
              onAfterPrint={() => setPrintView(false)}
            />
            <button className="btn btn-primary rounded-3" onClick={openAddModal}>
              + Add Attribute
            </button>
          </div>
        </div>

        <div className="px-4 mb-4">
          <div className="d-flex align-items-center justify-content-between gap-3 px-4 rounded-4 border" style={{ backgroundColor: "#faf8f5" }}>
            <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
            <ColumnToggle allColumns={allColumns} getToggleHideAllColumnsProps={getToggleHideAllColumnsProps} />
          </div>

          <div style={{ margin: printView ? "50px" : null }} ref={componentRef}>
            {printView && (
              <>
                <div style={{ padding: "1.5rem" }}><h2>Attributes</h2></div>
                <div style={{ marginBottom: "1rem", textAlign: "right", fontSize: "1.5rem" }}>
                  Date: {new Date().toLocaleDateString()}
                </div>
              </>
            )}

            <div className="table-responsive rounded-4 overflow-hidden mt-5 border" style={{ backgroundColor: "#ffffff" }}>
              <table className="table table-striped table-hover caption-top fs-4" {...getTableProps()} style={{margin: printView ? "0px" : "0" }}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
        <div>
          <ul className="pagination justify-content-center pagination-lg fs-4">
            <span style={{ padding: "5px" }}>
              page{' '}
              <strong>
                <em style={{ color: 'blue' }}>{pageIndex + 1}</em> of {pageOptions.length}
              </strong>
            </span>
            <select
              className="form-select form-select-lg fs-4"
              style={{ maxWidth: '110px' }}
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
            >
              {[10, 15, 20, 30, 40, 50, data.length].map((pageSize, index) => (
                <option key={index} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
            <li>
              <button className="page-link" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button>
            </li>
            <li className={`page-item ${!canPreviousPage ? 'disabled' : ''}`}>
              <button className="page-link" onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
            </li>
            <li className={`page-item ${!canNextPage ? 'disabled' : ''}`}>
              <button className="page-link" onClick={nextPage} disabled={!canNextPage}>Next</button>
            </li>
            <li>
              <button className="page-link" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button>
            </li>
          </ul>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};